import React, { useState, useRef, useEffect } from 'react';
import Draggable from 'react-draggable';
import html2canvas from 'html2canvas';

const FONT_FAMILIES = [
  'Arial', 'Georgia', 'Times New Roman', 'Comic Sans MS', 'Courier New', 'Verdana', 'Tahoma', 'Impact'
];

// Emoji/SVG stickers
const STICKERS = [
  { id: 'star', label: 'Star', emoji: '⭐' },
  { id: 'squid', label: 'Squid', emoji: '🦑' },
  { id: 'cake', label: 'Cake', emoji: '🎂' },
  { id: 'balloon', label: 'Balloon', emoji: '🎈' },
  { id: 'gift', label: 'Gift', emoji: '🎁' },
];

export default function CardDesigner() {
  const [bgColor, setBgColor] = useState('#ffffff'); // Default to white
  const [bgImage, setBgImage] = useState(null);
  const [elements, setElements] = useState([
    { id: 1, type: 'text', text: 'Happy Birthday, {employeeName}!', x: 50, y: 50, color: '#222', fontSize: 24, fontFamily: 'Arial', bold: false, italic: false, underline: false, align: 'left', locked: false, z: 1 }
  ]);
  const [selected, setSelected] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [preview, setPreview] = useState(false);
  const cardRef = useRef();
  const exportRef = useRef();
  const [dragGrid, setDragGrid] = useState(true);
  const [resizeDirection, setResizeDirection] = useState(null); // 'horizontal' | 'vertical' | null
  const [drawMode, setDrawMode] = useState(false);
  const [cardRect, setCardRect] = useState({ x: 50, y: 50, width: 400, height: 250, drawn: false });
  const [drawing, setDrawing] = useState(false);
  const drawStart = useRef({ x: 0, y: 0 });
  const [cardSelected, setCardSelected] = useState(false);
  const [history, setHistory] = useState([]);

  // Helper to push to history
  const pushHistory = (prev) => setHistory(h => [...h, prev]);

  // Wrap all setElements calls that change state with pushHistory
  const setElementsWithHistory = (updater) => {
    setElements(prev => {
      pushHistory(prev);
      return typeof updater === 'function' ? updater(prev) : updater;
    });
  };

  // Undo handler
  const handleUndo = () => {
    setHistory(h => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setElements(prev);
      return h.slice(0, -1);
    });
  };

  // Keyboard shortcut for undo
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        handleUndo();
      }
      if (e.key === 'Delete' && selected) {
        setElementsWithHistory(els => els.filter(el => el.id !== selected));
        setSelected(null);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selected]);

  // Save/load helpers
  const saveCard = () => {
    localStorage.setItem('birthdayCardDesign', JSON.stringify({ bgColor, bgImage, elements }));
    alert('Card design saved!');
  };

  const loadCard = () => {
    const data = localStorage.getItem('birthdayCardDesign');
    if (data) {
      const { bgColor, bgImage, elements } = JSON.parse(data);
      setBgColor(bgColor);
      setBgImage(bgImage);
      setElements(elements);
    } else {
      alert('No saved card found.');
    }
  };

  const resetCard = () => {
    setBgColor('#f9e7e7');
    setBgImage(null);
    setElements([{ id: 1, type: 'text', text: 'Happy Birthday, Buddy!', x: 50, y: 50, color: '#222', fontSize: 24, fontFamily: 'Arial', bold: false, italic: false, underline: false, align: 'left', locked: false, z: 1 }]);
    setSelected(null);
  };

  // Export as image (high quality, no border)
  const exportAsImage = async () => {
    if (!exportRef.current) return;
    setExporting(true);
    const prevSelected = selected;
    setSelected(null);
    // Wait for the border to disappear
    await new Promise(r => setTimeout(r, 50));
    // Create a temporary node with the card's size and content
    const exportNode = document.createElement('div');
    exportNode.style.position = 'absolute';
    exportNode.style.left = '0';
    exportNode.style.top = '0';
    exportNode.style.width = cardRect.width + 'px';
    exportNode.style.height = cardRect.height + 'px';
    exportNode.style.background = bgImage ? `url(${bgImage}) center/cover` : bgColor;
    exportNode.style.overflow = 'hidden';
    exportNode.style.border = 'none';
    // Copy the card's HTML
    exportNode.innerHTML = exportRef.current.innerHTML;
    document.body.appendChild(exportNode);
    await html2canvas(exportNode, { width: cardRect.width, height: cardRect.height, scale: 2, backgroundColor: '#fff', useCORS: true }).then(canvas => {
      const link = document.createElement('a');
      link.download = 'birthday_card.png';
      link.href = canvas.toDataURL();
      link.click();
    });
    document.body.removeChild(exportNode);
    setSelected(prevSelected);
    setExporting(false);
  };

  // Download JSON
  const downloadJSON = () => {
    const data = JSON.stringify({ bgColor, bgImage, elements }, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'birthday_card_design.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Upload JSON
  const uploadJSON = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { bgColor, bgImage, elements } = JSON.parse(e.target.result);
        setBgColor(bgColor);
        setBgImage(bgImage);
        setElements(elements);
      } catch {
        alert('Invalid card design file.');
      }
    };
    reader.readAsText(file);
  };

  // Send to employee (placeholder)
  const sendToEmployee = () => {
    alert('Send to employee feature coming soon!');
  };

  // Add/duplicate/delete/resize/lock/layer
  const addText = () => {
    setElementsWithHistory([...elements, {
      id: Date.now(),
      type: 'text',
      text: 'New Text',
      x: 100,
      y: 100,
      color: '#222',
      fontSize: 20,
      fontFamily: 'Arial',
      bold: false,
      italic: false,
      underline: false,
      align: 'left',
      locked: false,
      z: elements.length + 1
    }]);
  };
  const addImage = (file) => {
    const url = URL.createObjectURL(file);
    setElementsWithHistory([...elements, {
      id: Date.now(),
      type: 'image',
      src: url,
      x: 120,
      y: 120,
      width: 80,
      height: 80,
      locked: false,
      z: elements.length + 1
    }]);
  };

  const addSticker = (sticker) => {
    setElementsWithHistory([...elements, {
      id: Date.now(),
      type: 'emoji',
      emoji: sticker.emoji,
      x: 150,
      y: 150,
      fontSize: 48,
      locked: false,
      z: elements.length + 1
    }]);
  };

  const duplicateSelected = () => {
    const el = elements.find(e => e.id === selected);
    if (el) {
      setElementsWithHistory([...elements, { ...el, id: Date.now(), x: el.x + 20, y: el.y + 20, z: elements.length + 1 }]);
    }
  };

  const handleDrag = (i, e, data) => {
    if (elements[i].locked) return;
    // Snap to grid if enabled
    const grid = dragGrid ? 10 : 1;
    const x = Math.round(data.x / grid) * grid;
    const y = Math.round(data.y / grid) * grid;
    setElementsWithHistory(els => els.map((el, idx) => idx === i ? { ...el, x, y } : el));
  };

  const updateSelected = (updates) => {
    setElementsWithHistory(els => els.map(el => el.id === selected ? { ...el, ...updates } : el));
  };

  const bringForward = () => {
    setElementsWithHistory(els => {
      const idx = els.findIndex(el => el.id === selected);
      if (idx === -1 || idx === els.length - 1) return els;
      const newEls = [...els];
      [newEls[idx], newEls[idx + 1]] = [newEls[idx + 1], newEls[idx]];
      return newEls;
    });
  };

  const sendBackward = () => {
    setElementsWithHistory(els => {
      const idx = els.findIndex(el => el.id === selected);
      if (idx <= 0) return els;
      const newEls = [...els];
      [newEls[idx], newEls[idx - 1]] = [newEls[idx - 1], newEls[idx]];
      return newEls;
    });
  };

  const lockSelected = () => updateSelected({ locked: true });
  const unlockSelected = () => updateSelected({ locked: false });

  // Background image
  const handleBgImage = (file) => {
    const url = URL.createObjectURL(file);
    setBgImage(url);
  };
  
  const removeBgImage = () => setBgImage(null);

  // Resize handler for text/emoji
  const handleResize = (el, e, direction = 'se') => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startFontSize = el.fontSize;
    const startWidth = el.width || 120;
    const startHeight = el.height || 32;
    const isText = el.type === 'text';
    const isEmoji = el.type === 'emoji';
    setResizeDirection('diagonal'); // Always show diagonal for text
    const onMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      let updates = {};
      if (isText) {
        // Always update both width and height
        let newWidth = Math.max(40, startWidth + deltaX);
        let newHeight = Math.max(20, startHeight + deltaY);
        updates.width = newWidth;
        updates.height = newHeight;
      } else if (isEmoji) {
        // For emoji, scale by the larger delta
        let newFontSize = startFontSize + Math.max(deltaX, deltaY);
        newFontSize = Math.max(8, Math.min(200, newFontSize));
        updates.fontSize = newFontSize;
      }
      setElementsWithHistory(els => els.map(e2 => e2.id === el.id ? { ...e2, ...updates } : e2));
    };
    const onUp = () => {
      setResizeDirection(null);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Mouse handlers for drawing card area
  const handleCanvasMouseDown = (e) => {
    if (!drawMode) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    drawStart.current = { x, y };
    setCardRect(r => ({ ...r, x, y, width: 0, height: 0, drawn: false }));
    setDrawing(true);
  };
  const handleCanvasMouseMove = (e) => {
    if (!drawMode || !drawing) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const start = drawStart.current;
    setCardRect(r => ({
      ...r,
      x: Math.min(start.x, x),
      y: Math.min(start.y, y),
      width: Math.abs(x - start.x),
      height: Math.abs(y - start.y),
      drawn: false
    }));
  };
  const handleCanvasMouseUp = (e) => {
    if (!drawMode || !drawing) return;
    setDrawing(false);
    setCardRect(r => ({ ...r, drawn: true }));
    setDrawMode(false);
  };

  // Controls for selected element
  const selectedEl = elements.find(el => el.id === selected);

  // Add the handleCardResizeStart function to handle resizing logic for the card area
  const handleCardResizeStart = (e, dir) => {
    e.stopPropagation();
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = cardRect.width;
    const startHeight = cardRect.height;

    const onMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      let updates = {};
      if (dir === 'nw') {
        updates.x = Math.max(0, startX + deltaX);
        updates.y = Math.max(0, startY + deltaY);
        updates.width = Math.max(100, startWidth - deltaX); // Minimum width
        updates.height = Math.max(100, startHeight - deltaY); // Minimum height
      } else if (dir === 'ne') {
        updates.x = Math.max(0, startX + deltaX);
        updates.y = Math.max(0, startY + deltaY);
        updates.width = Math.max(100, startWidth + deltaX);
        updates.height = Math.max(100, startHeight - deltaY);
      } else if (dir === 'sw') {
        updates.x = Math.max(0, startX + deltaX);
        updates.y = Math.max(0, startY + deltaY);
        updates.width = Math.max(100, startWidth - deltaX);
        updates.height = Math.max(100, startHeight + deltaY);
      } else if (dir === 'se') {
        updates.x = Math.max(0, startX + deltaX);
        updates.y = Math.max(0, startY + deltaY);
        updates.width = Math.max(100, startWidth + deltaX);
        updates.height = Math.max(100, startHeight + deltaY);
      }
      setCardRect(r => ({ ...r, ...updates }));
    };

    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  return (
    <div className="mx-auto p-6">
      <h2 className="text-2xl font-bold mb-4 text-center">Birthday Card Designer</h2>
      <div className="flex flex-row gap-6 items-start justify-center">
        {/* Left Sidebar: Main Tools */}
        <div className="flex flex-col gap-3 bg-gray-50 p-4 rounded-lg shadow ">
          <label className="font-semibold mb-1">Background:</label>
          <input type="color" value={bgColor} onChange={e => setBgColor(e.target.value)} />
          <input type="file" accept="image/*" onChange={e => handleBgImage(e.target.files[0])} />
          {bgImage && <button onClick={removeBgImage} className="text-xs text-red-500">Remove BG Image</button>}
          <button onClick={addText} className="bg-blue-600 text-white px-2 py-1 rounded" disabled={!cardRect.drawn} title={!cardRect.drawn ? 'Draw card area first' : ''}>Add Text</button>
          <input type="file" accept="image/*" onChange={e => cardRect.drawn && addImage(e.target.files[0])} disabled={!cardRect.drawn} title={!cardRect.drawn ? 'Draw card area first' : ''} />
          <span className="font-semibold mt-2">Stickers:</span>
          <div className="flex gap-2 flex-wrap">
            {STICKERS.map(sticker => (
              <span
                key={sticker.id}
                style={{ fontSize: 32, cursor: cardRect.drawn ? 'pointer' : 'not-allowed', border: '1px solid #eee', borderRadius: 4, display: 'inline-block', width: 32, height: 32, textAlign: 'center', lineHeight: '32px', background: '#fff', opacity: cardRect.drawn ? 1 : 0.5 }}
                title={cardRect.drawn ? sticker.label : 'Draw card area first'}
                onClick={() => cardRect.drawn && addSticker(sticker)}
              >
                {sticker.emoji}
              </span>
            ))}
          </div>
          <button onClick={saveCard} className="bg-green-600 text-white px-2 py-1 rounded mt-2">Save</button>
          <button onClick={loadCard} className="bg-yellow-600 text-white px-2 py-1 rounded">Load</button>
          <button onClick={downloadJSON} className="bg-gray-700 text-white px-2 py-1 rounded">Download JSON</button>
          <input type="file" accept="application/json" onChange={e => uploadJSON(e.target.files[0])} />
          <button onClick={exportAsImage} className="bg-purple-600 text-white px-2 py-1 rounded">Export as Image</button>
          <button onClick={resetCard} className="bg-gray-400 text-white px-2 py-1 rounded">Reset</button>
          <button onClick={sendToEmployee} className="bg-pink-600 text-white px-2 py-1 rounded">Send to Employee</button>
          <label className="mt-2 text-xs flex items-center gap-1">
            <input type="checkbox" checked={dragGrid} onChange={e => setDragGrid(e.target.checked)} /> Snap to grid
          </label>
          <button onClick={() => setPreview(p => !p)} className="bg-blue-200 text-blue-800 px-2 py-1 rounded text-xs mt-2">{preview ? 'Edit Mode' : 'Preview'}</button>
          <button onClick={() => setDrawMode(true)} className="bg-blue-500 text-white px-2 py-1 rounded">Draw Card Area</button>
          {drawMode && <span className="text-xs text-blue-700">Click and drag on canvas to draw card</span>}
          <button onClick={handleUndo} className="bg-gray-300 text-black px-2 py-1 rounded" disabled={history.length === 0}>Undo</button>
        </div>

        {/* Center: Canvas for drawing card */}
        <div className="flex flex-col items-center justify-center flex-1">
          <div
            style={{ width: 800, height: 600, background: '#f3f3f3', position: 'relative', border: '1px solid #ddd', cursor: drawMode ? 'crosshair' : 'default' }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
          >
            {/* Draw the card only if drawn or drawing */}
            {(cardRect.drawn || drawing) ? (
              <Draggable
                position={{ x: cardRect.x, y: cardRect.y }}
                onStop={(e, data) => {
                  setCardRect(r => ({ ...r, x: data.x, y: data.y }));
                  setCardSelected(true);
                }}
                disabled={drawMode}
                cancel=".element-draggable"
              >
                <div
                  ref={cardRef}
                  className="absolute border rounded-lg shadow group"
                  style={{
                    width: cardRect.width,
                    height: cardRect.height,
                    background: bgImage ? `url(${bgImage}) center/cover` : bgColor,
                    overflow: 'hidden',
                    border: cardSelected ? '2px dashed #0077fe' : '1px solid #bbb',
                    zIndex: 100,
                    boxSizing: 'border-box',
                  }}
                  onMouseDown={e => { if (!drawMode && e.currentTarget === e.target) { setSelected(null); setCardSelected(true); } }}
                >
                  {/* Delete button */}
                  {cardSelected && (
                    <button
                      style={{ position: 'absolute', top: 4, right: 4, zIndex: 200, background: '#fff', border: '1px solid #bbb', borderRadius: 4, padding: 2, cursor: 'pointer' }}
                      title="Delete card area"
                      onClick={e => { e.stopPropagation(); setCardRect({ x: 50, y: 50, width: 400, height: 250, drawn: false }); setElements([]); setCardSelected(false); }}
                    >
                      🗑️
                    </button>
                  )}
                  {/* Resize handles (corners) */}
                  {cardSelected && ['nw','ne','sw','se'].map(dir => (
                    <div
                      key={dir}
                      style={{
                        position: 'absolute',
                        width: 12,
                        height: 12,
                        background: '#0077fe',
                        borderRadius: 6,
                        zIndex: 200,
                        cursor: dir === 'nw' ? 'nwse-resize' : dir === 'ne' ? 'nesw-resize' : dir === 'sw' ? 'nesw-resize' : 'nwse-resize',
                        left: dir[1] === 'w' ? -6 : undefined,
                        right: dir[1] === 'e' ? -6 : undefined,
                        top: dir[0] === 'n' ? -6 : undefined,
                        bottom: dir[0] === 's' ? -6 : undefined,
                      }}
                      onMouseDown={e => handleCardResizeStart(e, dir)}
                    />
                  ))}
                  {/* Card content */}
                  <div ref={exportRef} style={{ width: '100%', height: '100%', background: bgImage ? `url(${bgImage}) center/cover` : bgColor, overflow: 'hidden', border: 'none', position: 'absolute', top: 0, left: 0 }}>
                    {/* All elements, positioned relative to cardRect.x/y */}
                    {elements.map((el, i) => (
                      <Draggable
                        key={el.id}
                        position={{ x: el.x, y: el.y }}
                        onStop={(e, data) => handleDrag(i, e, data)}
                        disabled={el.locked || preview}
                        grid={dragGrid ? [10, 10] : [1, 1]}
                      >
                        <div
                          className="element-draggable"
                          style={{
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            cursor: el.locked || preview ? 'default' : 'move',
                            zIndex: i + 1,
                            opacity: preview ? 1 : (selected === el.id ? 1 : 0.95),
                            pointerEvents: preview ? 'none' : 'auto',
                            userSelect: preview ? 'none' : 'auto',
                            border: (selected && selected === el.id && !preview && !exporting) ? '1px dashed #0077fe' : 'none',
                            boxShadow: el.locked ? '0 0 0 2px #fbbf24' : 'none',
                            background: el.type === 'text' && selected === el.id && !preview ? '#fff8' : 'transparent',
                            minWidth: 40,
                            minHeight: 20,
                            width: el.type === 'text' ? (el.width || 120) : undefined,
                            height: el.type === 'text' ? (el.height || 32) : undefined,
                          }}
                          onClick={e => { e.stopPropagation(); !preview && setSelected(el.id); }}
                        >
                          {el.type === 'text' ? (
                            <span
                              style={{
                                color: el.color,
                                fontSize: el.fontSize,
                                fontFamily: el.fontFamily,
                                fontWeight: el.bold ? 'bold' : 'normal',
                                fontStyle: el.italic ? 'italic' : 'normal',
                                textDecoration: el.underline ? 'underline' : 'none',
                                textAlign: el.align,
                                display: 'inline-block',
                                minWidth: 40,
                                minHeight: 20,
                                width: el.width || 120,
                                height: el.height || 32,
                                overflow: 'hidden',
                                whiteSpace: 'pre-line',
                                wordBreak: 'break-word',
                              }}
                              contentEditable={selected === el.id && !el.locked && !preview}
                              suppressContentEditableWarning
                              onBlur={e => setElementsWithHistory(els => els.map((e2, idx) => idx === i ? { ...e2, text: e.target.innerText } : e2))}
                            >
                              {el.text}
                            </span>
                          ) : el.type === 'emoji' ? (
                            <span
                              style={{ fontSize: el.fontSize, display: 'inline-block', userSelect: 'none' }}
                              role="img"
                              aria-label="sticker"
                            >
                              {el.emoji}
                            </span>
                          ) : (
                            <img
                              src={el.src}
                              alt=""
                              crossOrigin="anonymous"
                              style={{ width: el.width, height: el.height, borderRadius: 8 }}
                              draggable={false}
                              onClick={() => !preview && setSelected(el.id)}
                            />
                          )}
                          {/* Resize handle for text/emoji */}
                          {selected === el.id && (el.type === 'text' || el.type === 'emoji') && !preview && !el.locked && (
                            <div
                              style={{
                                position: 'absolute',
                                right: -10,
                                bottom: -10,
                                width: 16,
                                height: 16,
                                background: '#0077fe',
                                borderRadius: 8,
                                cursor: 'nwse-resize',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: 'bold',
                                zIndex: 10,
                                userSelect: 'none',
                              }}
                              title="Resize"
                              onMouseDown={e => handleResize(el, e)}
                            >
                              '↘'
                            </div>
                          )}
                          {el.locked && !preview && <span style={{ position: 'absolute', top: -10, right: -10, color: '#fbbf24', fontSize: 18 }}>🔒</span>}
                        </div>
                      </Draggable>
                    ))}
                  </div>
                </div>
              </Draggable>
            ) : (
              <div className="flex items-center justify-center w-full h-full text-gray-400 text-lg">Draw your card area using the button on the left</div>
            )}
          </div>
        </div>

        {/* Right Sidebar: Selected Element Controls */}
        {!preview && (
          <div className="flex flex-col gap-3 bg-gray-50 p-4 rounded-lg shadow min-w-[220px] max-w-[260px]">
            <span className="font-semibold">Selected:</span>
            {selectedEl ? (
              <>
                <button className="text-red-500" onClick={() => setElementsWithHistory(els => els.filter(el => el.id !== selected))}>Delete</button>
                <button className="text-blue-500" onClick={duplicateSelected}>Duplicate</button>
                <button className="text-gray-700" onClick={bringForward}>Bring Forward</button>
                <button className="text-gray-700" onClick={sendBackward}>Send Backward</button>
                {selectedEl.locked ? (
                  <button className="text-yellow-600" onClick={unlockSelected}>Unlock</button>
                ) : (
                  <button className="text-yellow-600" onClick={lockSelected}>Lock</button>
                )}
                {selectedEl.type === 'text' && (
                  <>
                    <label>Font Size:</label>
                    <input
                      type="number"
                      min={8}
                      max={72}
                      value={selectedEl.fontSize}
                      onChange={e => updateSelected({ fontSize: parseInt(e.target.value) })}
                      className="w-16 border rounded px-1"
                    />
                    <label>Font Family:</label>
                    <select
                      value={selectedEl.fontFamily}
                      onChange={e => updateSelected({ fontFamily: e.target.value })}
                      className="border rounded px-1"
                    >
                      {FONT_FAMILIES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <label>Color:</label>
                    <input
                      type="color"
                      value={selectedEl.color}
                      onChange={e => updateSelected({ color: e.target.value })}
                    />
                    <div className="flex gap-2 mt-2">
                      <button className={selectedEl.bold ? 'font-bold text-blue-600' : ''} onClick={() => updateSelected({ bold: !selectedEl.bold })}>B</button>
                      <button className={selectedEl.italic ? 'italic text-blue-600' : ''} onClick={() => updateSelected({ italic: !selectedEl.italic })}>I</button>
                      <button className={selectedEl.underline ? 'underline text-blue-600' : ''} onClick={() => updateSelected({ underline: !selectedEl.underline })}>U</button>
                    </div>
                    <label>Align:</label>
                    <select value={selectedEl.align} onChange={e => updateSelected({ align: e.target.value })}>
                      <option value="left">Left</option>
                      <option value="center">Center</option>
                      <option value="right">Right</option>
                    </select>
                  </>
                )}
                {selectedEl.type === 'image' && (
                  <>
                    <label>Width:</label>
                    <input
                      type="number"
                      min={20}
                      max={400}
                      value={selectedEl.width}
                      onChange={e => updateSelected({ width: parseInt(e.target.value) })}
                      className="w-16 border rounded px-1"
                    />
                    <label>Height:</label>
                    <input
                      type="number"
                      min={20}
                      max={400}
                      value={selectedEl.height}
                      onChange={e => updateSelected({ height: parseInt(e.target.value) })}
                      className="w-16 border rounded px-1"
                    />
                  </>
                )}
                {selectedEl.type === 'emoji' && (
                  <>
                    <label>Size:</label>
                    <input
                      type="number"
                      min={16}
                      max={200}
                      value={selectedEl.fontSize}
                      onChange={e => updateSelected({ fontSize: parseInt(e.target.value) })}
                      className="w-16 border rounded px-1"
                    />
                  </>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-sm">No element selected. Click on an element to edit it.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
} 